import { useCallback, useEffect, useState } from 'react';

import { addYears, isWithinInterval, startOfToday } from 'date-fns';
import { difference, orderBy } from 'lodash';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import type {
  OrganizationalUnitDistribution,
  PlanUnit,
  WorkAgreementDetails
} from 'components/types';

import { getDistributionTitle } from './utils';
import { getMaxDate } from 'utils/date';

import { getPlanUnits } from 'RestAPIs/cost-centers';
import { getOrganizationalUnitDistributions } from 'RestAPIs/employments/organizational-unit-distributions';

import Button from '@mui/material/Button';

import { Accordion } from 'components/shared';

import OrganizationalUnitDistributionEntry from './OrganizationalUnitDistributionEntry';
import Styles from './Styles';

type Props = {
  workAgreement: WorkAgreementDetails;
  workAgreements: WorkAgreementDetails[];
  canEdit: boolean;
};

export default function PlanUnitDistribution({
  workAgreement,
  workAgreements,
  canEdit
}: Props) {
  'use memo';
  const { t } = useTranslation('common');
  const [organizationalUnitDistributions, setOrganizationalUnitDistributions] =
    useState<OrganizationalUnitDistribution[]>([]);
  const [
    historyOrganizationalUnitDistributions,
    setHistoryOrganizationalUnitDistributions
  ] = useState<OrganizationalUnitDistribution[]>([]);
  const [expandedStates, setExpandedStates] = useState<boolean[]>([]);
  const [planUnits, setPlanUnits] = useState<PlanUnit[]>([]);

  const onSelectedDistribution = (index: number) => {
    const newStates = [...expandedStates];
    newStates[index] = !newStates[index];
    setExpandedStates(newStates);
  };

  const showHistory = () => {
    setOrganizationalUnitDistributions([
      ...historyOrganizationalUnitDistributions,
      ...organizationalUnitDistributions
    ]);
    setHistoryOrganizationalUnitDistributions([]);
  };

  const loadOrganizationalUnitDistributions = useCallback(() => {
    getOrganizationalUnitDistributions(workAgreement.waId).then(
      ({ data: organizationalUnitDistributions }) => {
        if (organizationalUnitDistributions.length) {
          organizationalUnitDistributions.forEach((o) => {
            // o.id = uuidv4(); // provide an id, BE won't it
            o.sharedOrganizationalUnits.forEach((o) => {
              o.id = uuidv4(); // provide an id, BE won't it
            });
          });
          const today = startOfToday();
          const visibleOrganizationalUnitDistributions = orderBy(
            organizationalUnitDistributions.filter(
              ({ startDate, endDate }) =>
                new Date(startDate) >= today ||
                isWithinInterval(today, {
                  start: new Date(startDate),
                  end: endDate ? new Date(endDate) : getMaxDate()
                })
            ),
            ({ startDate }) => new Date(startDate),
            'desc'
          );

          const history = difference(
            organizationalUnitDistributions,
            visibleOrganizationalUnitDistributions
          );

          if (visibleOrganizationalUnitDistributions.length) {
            setOrganizationalUnitDistributions(
              visibleOrganizationalUnitDistributions
            );
            const states = new Array(
              visibleOrganizationalUnitDistributions.length
            ).fill(false);
            const unitToShow = orderBy(
              visibleOrganizationalUnitDistributions,
              (d) => new Date(d.startDate),
              'asc'
            )[0];
            if (unitToShow) {
              states[
                visibleOrganizationalUnitDistributions.indexOf(unitToShow)
              ] = true;
            }
            setExpandedStates(states);
            setHistoryOrganizationalUnitDistributions(history);
          } else {
            setOrganizationalUnitDistributions(history);
            setHistoryOrganizationalUnitDistributions([]);
          }
        }
      }
    );
  }, [workAgreement]);

  useEffect(() => {
    setExpandedStates([]);
    setOrganizationalUnitDistributions([]);
    setHistoryOrganizationalUnitDistributions([]);
    loadOrganizationalUnitDistributions();
  }, [loadOrganizationalUnitDistributions]);

  useEffect(() => {
    getPlanUnits({
      startDate: new Date(),
      endDate: addYears(new Date(), 1),
      type: 'public'
    }).then(({ data }) => {
      data.forEach((u) => {
        u.id = u.brId;
      });
      setPlanUnits(data);
    });
  }, []);

  const orderedDistributions = orderBy(
    organizationalUnitDistributions,
    ({ startDate }) => new Date(startDate),
    'desc'
  );

  return (
    <Styles>
      {orderedDistributions.map((distribution, i) => (
        <Accordion
          key={distribution.id}
          summary={getDistributionTitle(distribution)}
          expanded={Boolean(expandedStates[i])}
          onSelected={() => onSelectedDistribution(i)}
        >
          <OrganizationalUnitDistributionEntry
            {...distribution}
            workAgreementId={workAgreement.waId}
            loadOrganizationalUnitDistributions={
              loadOrganizationalUnitDistributions
            }
            planUnits={planUnits}
            canEdit={canEdit}
            allDistributions={orderedDistributions}
            workAgreements={workAgreements}
          />
        </Accordion>
      ))}
      {historyOrganizationalUnitDistributions.length > 0 && (
        <div className="show-history">
          <Button variant="outlined" size="small" onClick={showHistory}>
            {t('showHistory')}
          </Button>
        </div>
      )}
    </Styles>
  );
}
